import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'
import { ScanIcon } from '../_icons/ScanIcon'
import { CameraScanButton } from '../CameraScan'
import { useScanInput } from '../../../hooks/useScanInput'
import { scanSearchPanelStyles as s } from './ScanSearchPanel.styles'
export interface ScanOption {
  key: string
  label: string
  placeholder: string
}

interface ScanSearchPanelProps {
  /** List of toggle options. Each adds one tab to the switch. */
  options: ScanOption[]
  /** Called when the user clicks "Buscar" or presses Enter. */
  onSearch: (value: string, option: ScanOption) => void
  /** Label displayed above the panel. */
  hintLabel?: string
  /** Text for the search button. */
  searchLabel?: string
  /** Disable the whole panel (e.g. while loading). */
  disabled?: boolean
  /** Extra sx overrides on the outer container. */
  sx?: SxProps<Theme>
  // Para mantener la referencia al autofocus del input
  inputRef?: React.RefObject<HTMLInputElement>
  /**
   * Agrega el boton de escaneo por camara debajo del input. Opt-in para no
   * cambiar el panel donde no se necesita.
   */
  includeCameraScan?: boolean
  /** Mensaje de error de camara ya traducido, para mostrarlo donde corresponda. */
  onCameraError?: (message: string) => void
}

const ScanSearchPanel: React.FC<ScanSearchPanelProps> = ({
  options,
  onSearch,
  hintLabel = 'Ingresa o escanea el dato',
  searchLabel = 'Buscar',
  disabled = false,
  sx,
  inputRef: externalRef,
  includeCameraScan = false,
  onCameraError
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [sliderStyle, setSliderStyle] = useState<{
    left: number
    width: number
  }>({ left: 0, width: 0 })

  const toggleGroupRef = useRef<HTMLDivElement | null>(null)
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([])

  // El activeIndex va por ref para que cambiar de pestana no tenga que
  // reconstruir los handlers del hook.
  const activeIndexRef = useRef(activeIndex)
  activeIndexRef.current = activeIndex

  /**
   * Cablea lector fisico, tipeo manual y camara sobre la misma busqueda.
   * trimOnChange en false para no cambiar el comportamiento previo del panel:
   * aca si se pueden escribir espacios mientras se tipea.
   */
  const scan = useScanInput({
    onSearch: (value) => onSearch(value, options[activeIndexRef.current]),
    disabled,
    trimOnChange: false,
    inputRef: externalRef
  })
  const inputRef = scan.fieldProps.inputRef

  const updateSlider = useCallback(() => {
    const group = toggleGroupRef.current
    const btn = btnRefs.current[activeIndex]
    if (!group || !btn) return
    const gRect = group.getBoundingClientRect()
    const bRect = btn.getBoundingClientRect()
    setSliderStyle({ left: bRect.left - gRect.left, width: bRect.width })
  }, [activeIndex])

  useEffect(() => {
    updateSlider()

    const observer = new ResizeObserver(() => updateSlider())
    if (toggleGroupRef.current) observer.observe(toggleGroupRef.current)

    window.addEventListener('resize', updateSlider)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', updateSlider)
    }
  }, [updateSlider])

  useEffect(() => {
    // En modo camara no se enfoca: abriria el teclado virtual.
    if (scan.autoFocus) inputRef.current?.focus()
  }, [activeIndex, scan.autoFocus])

  const handleSelect = (index: number) => {
    setActiveIndex(index)
    scan.reset()
  }

  const activeOption = options[activeIndex] ?? options[0]

  return (
    <Stack spacing={1.5} sx={sx}>
      <Typography variant='h6' component='h1' sx={s.hintLabel}>
        {hintLabel}
      </Typography>

      <Box sx={s.card}>
        {/* Toggle switch */}
        <Box ref={toggleGroupRef} sx={s.toggleGroup}>
          <Box sx={s.toggleSlider(sliderStyle.left, sliderStyle.width)} />
          {options.map((opt, i) => (
            <Box
              key={opt.key}
              component='button'
              ref={(el: HTMLButtonElement | null) => {
                btnRefs.current[i] = el
              }}
              onClick={() => handleSelect(i)}
              disabled={disabled}
              sx={s.toggleBtn(i === activeIndex)}
            >
              {opt.label}
            </Box>
          ))}
        </Box>

        {/* Text input */}
        <TextField
          {...scan.fieldProps}
          placeholder={activeOption?.placeholder ?? ''}
          disabled={disabled}
          autoComplete='off'
          fullWidth
          size='small'
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <ScanIcon />
              </InputAdornment>
            ),
            sx: s.inputRoot
          }}
          // El autoFocus lo decide fieldProps (false en modo camara, para no
          // abrir el teclado virtual), asi que aca no se fuerza.
          inputProps={{ sx: s.inputProps }}
        />

        {/* Camera scan (opt-in) */}
        {includeCameraScan && (
          <CameraScanButton
            {...scan.cameraProps}
            onError={onCameraError}
            disabled={disabled}
            dialogTitle={`Escanear ${activeOption?.label ?? ''}`}
            helperText={`Centra el código dentro del recuadro`}
          />
        )}

        {/* Search button */}
        <Button
          variant='contained'
          fullWidth
          disabled={!scan.value.trim() || disabled}
          onClick={scan.submit}
          disableElevation
        >
          {searchLabel}
        </Button>
      </Box>
    </Stack>
  )
}

export default ScanSearchPanel
